import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LockClockIcon from '@mui/icons-material/LockClock';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import React from 'react';
import { ReactComponent as AllianceLogo } from '../../assets/alliance/alliance-logo.svg';
import {
  ALLIANCE_APP_ORDER,
  ALLIANCE_APPS,
  AllianceAppKey,
} from './alliance-apps';
import { SIGN_IN_SOCIAL_LINKS } from './alliance-links';
import { pageSurface } from './alliance-surface';
import { ALLIANCE_THRESHOLDS } from './alliance-tokens';
import { APP_ICONS } from './app-icons';
import { useIsMobileLayout } from './bar-controls';
import { CURRENT_APP, CURRENT_APP_FONTS } from './current-app';

/**
 * Sign In page, layout A (approved Oct 1 2026): the app's bar color fills
 * the left panel, with the square photo uncropped and today's Andy-ism set
 * as type below it. Phones drop the photo and quote for a bar-colored band.
 */

/** Headline (with one emphasized phrase) and subline per app */
const TAGLINES: Record<
  AllianceAppKey,
  { before: string; em: string; after: string; sub: string }
> = {
  arc: {
    before: 'Your dashboard for ',
    em: 'your',
    after: ' business.',
    sub: 'Production, team, carriers and resources in one place.',
  },
  contracting: {
    before: 'Get contracted. ',
    em: 'Start writing.',
    after: '',
    sub: 'Contracts, writing numbers and carrier appointments.',
  },
  leads: {
    before: 'Your next client is ',
    em: 'one lead',
    after: ' away.',
    sub: 'Buy leads, manage subscriptions and track your pipeline.',
  },
  events: {
    before: 'Where the Alliance ',
    em: 'shows up',
    after: '.',
    sub: 'Events, trips and tickets for agents and guests.',
  },
  university: {
    before: 'Learn it today. ',
    em: 'Use it',
    after: ' tomorrow.',
    sub: 'Training, courses and the Video Vault.',
  },
};

/** Headline type per app: size desktop / phone, emphasis weight */
const TITLE: Record<
  AllianceAppKey,
  { size: number; sizeMobile: number; lineHeight: number; emWeight: number }
> = {
  arc: { size: 44, sizeMobile: 34, lineHeight: 1.12, emWeight: 700 },
  contracting: { size: 44, sizeMobile: 34, lineHeight: 1.12, emWeight: 700 },
  leads: { size: 44, sizeMobile: 34, lineHeight: 1.12, emWeight: 700 },
  events: { size: 48, sizeMobile: 38, lineHeight: 1.08, emWeight: 500 },
  university: { size: 64, sizeMobile: 50, lineHeight: 0.95, emWeight: 400 },
};

/** App-link hover color: each app's own accent (light / dark page) */
const LINK_HOVER: Record<AllianceAppKey, { light: string; dark: string }> = {
  arc: { light: '#1976d2', dark: '#00baff' },
  contracting: { light: '#00874a', dark: '#00C868' },
  leads: { light: '#0070c0', dark: '#33aaff' },
  events: { light: '#C62828', dark: '#ff6f60' },
  university: { light: '#CE0E2D', dark: '#ff7085' },
};

const DAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const MONTHS = [
  'JAN',
  'FEB',
  'MAR',
  'APR',
  'MAY',
  'JUN',
  'JUL',
  'AUG',
  'SEP',
  'OCT',
  'NOV',
  'DEC',
];

/**
 * Quote type size by length, so longer quotes stay at two or three lines
 * under the photo: 30px (the design) up to 60 characters, then smaller.
 */
const quoteFontSize = (text: string) =>
  text.length <= 60 ? 30 : text.length <= 80 ? 26 : 22;

/** "WED · OCT 1" for the label on the photo */
const photoDateLabel = (date: Date) =>
  `${DAYS[date.getDay()]} · ${MONTHS[date.getMonth()]} ${date.getDate()}`;

/**
 * @param onSignIn - Starts the Hosted UI redirect. If it throws, or the page
 *   is still here after 8s, the button shows "We couldn't start sign-in"
 * @param forgotPasswordHref - The Hosted UI forgot-password URL
 * @param signUpHref - Contracting sign-up; null hides the section (Events)
 * @param notice - "expired" shows the calm session-expired notice
 * @param photo - Today's square photo ({ src, alt })
 * @param quote - Today's Andy-ism ({ text, meaning })
 */
export function SignInPage(props: {
  onSignIn: () => Promise<unknown> | void;
  forgotPasswordHref: string;
  signUpHref: string | null;
  notice?: 'expired' | null;
  photo: { src: string; alt: string };
  quote: { text: string; meaning: string };
}) {
  // State
  const [status, setStatus] = React.useState<'idle' | 'pending' | 'failed'>(
    'idle',
  );
  // Ref
  const timerRef = React.useRef<number>();
  // Height of the quote, meaning and label under the photo (desktop), so a
  // long quote shrinks the photo instead of pushing it into the logo.
  // Measured a frame later: the narrower photo rewraps the caption, which
  // would otherwise resize it again inside the same observer pass
  const [captionHeight, setCaptionHeight] = React.useState(100);
  const captionObserver = React.useRef<ResizeObserver>();
  const captionRef = React.useCallback((node: HTMLElement | null) => {
    captionObserver.current?.disconnect();
    if (node) {
      captionObserver.current = new ResizeObserver(() =>
        window.requestAnimationFrame(() => setCaptionHeight(node.offsetHeight)),
      );
      captionObserver.current.observe(node);
    }
  }, []);
  // Hooks
  const isMobile = useIsMobileLayout();

  React.useEffect(() => () => window.clearTimeout(timerRef.current), []);

  const c = pageSurface();
  const key = CURRENT_APP.key;
  const tagline = TAGLINES[key];
  const title = TITLE[key];
  const titleFont = CURRENT_APP_FONTS.title as React.CSSProperties;
  const AppIcon = APP_ICONS[key];
  const pending = status === 'pending';
  // Photo size that fits under the logo with the caption below it
  const photoFit = `calc(100dvh - ${217 + captionHeight}px)`;

  const handleSignIn = async () => {
    if (pending) {
      return;
    }
    setStatus('pending');
    window.clearTimeout(timerRef.current);
    // The redirect normally unloads the page; if it hasn't, it failed
    timerRef.current = window.setTimeout(
      () => setStatus('failed'),
      ALLIANCE_THRESHOLDS.signInFailureMs,
    );
    try {
      await props.onSignIn();
    } catch (error) {
      window.clearTimeout(timerRef.current);
      setStatus('failed');
    }
  };

  const linkStyle = (hover: string, active?: boolean) => ({
    color: active ? c.text : c.text2,
    textDecoration: 'none',
    transition: 'color 0.15s',
    '&:hover': { color: hover },
  });

  const actions = (
    <Box
      sx={{
        width: '100%',
        maxWidth: isMobile ? '100%' : 400,
        display: 'flex',
        flexDirection: 'column',
        gap: isMobile ? 4 : '36px',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '8px',
              backgroundColor: CURRENT_APP.bar,
              border: '1px solid #ffffff1a',
              boxSizing: 'border-box',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: CURRENT_APP.accentOnBar,
            }}
          >
            <AppIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box
            sx={{
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: '.12em',
              textTransform: 'uppercase',
              color: c.text2,
            }}
          >
            {CURRENT_APP.fullName}
          </Box>
        </Box>
        <Box
          component="h1"
          sx={{
            m: 0,
            ...titleFont,
            fontSize: isMobile ? title.sizeMobile : title.size,
            lineHeight: title.lineHeight,
            letterSpacing: c.title.letterSpacing,
            textWrap: 'balance',
          }}
        >
          {tagline.before}
          <Box
            component="span"
            sx={{ color: c.link, fontWeight: title.emWeight }}
          >
            {tagline.em}
          </Box>
          {tagline.after}
        </Box>
        <Box sx={{ fontSize: 16, lineHeight: 1.5, color: c.text2 }}>
          {tagline.sub}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {props.notice === 'expired' ? (
          <Box
            role="status"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              p: '12px 14px',
              borderRadius: '12px',
              border: `1px solid ${c.border}`,
              backgroundColor: c.dark ? '#ffffff0d' : '#0000000a',
              fontSize: 14,
              lineHeight: 1.45,
            }}
          >
            <LockClockIcon sx={{ fontSize: 20, color: c.text2 }} />
            <Box sx={{ flex: 1 }}>
              Your session expired. Please sign in again.
            </Box>
          </Box>
        ) : null}

        <ButtonBase
          onClick={handleSignIn}
          aria-disabled={pending}
          aria-busy={pending}
          sx={{
            fontFamily: 'inherit',
            height: 52,
            borderRadius: '14px',
            backgroundColor: c.primary.bg,
            color: c.primary.fg,
            justifyContent: 'space-between',
            p: '0 18px 0 20px',
            fontSize: 16,
            fontWeight: 500,
            opacity: pending ? 0.75 : 1,
            cursor: pending ? 'progress' : 'pointer',
            '&:hover': { filter: 'brightness(1.08)' },
          }}
        >
          {pending ? 'Opening sign-in…' : 'Sign in'}
          {pending ? (
            <CircularProgress
              size={18}
              thickness={5}
              sx={{ color: c.primary.fg }}
            />
          ) : (
            <ArrowForwardIcon sx={{ fontSize: 20 }} />
          )}
        </ButtonBase>

        {status === 'failed' ? (
          <Box
            role="alert"
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              fontSize: 14,
              lineHeight: 1.45,
              color: c.dark ? '#ff8a80' : '#c62828',
            }}
          >
            <ErrorOutlineIcon sx={{ fontSize: 18, mt: '1px' }} />
            <div>We couldn’t start sign-in. Please try again.</div>
          </Box>
        ) : null}

        <Box
          component="a"
          href={props.forgotPasswordHref}
          sx={{
            alignSelf: 'flex-start',
            fontSize: 14,
            fontWeight: 500,
            color: c.dark ? '#ffffff' : CURRENT_APP.bar,
            textDecoration: 'underline',
            textUnderlineOffset: '3px',
            textDecorationColor: c.border2,
          }}
        >
          Forgot password?
        </Box>
      </Box>

      {props.signUpHref ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            pt: 3,
            borderTop: `1px solid ${c.border}`,
          }}
        >
          <Box sx={{ fontSize: 14, color: c.text2 }}>New to The Alliance?</Box>
          <Box
            component="a"
            href={props.signUpHref}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              height: 40,
              px: '18px',
              borderRadius: '10px',
              border: `1px solid ${c.border2}`,
              color: c.text,
              display: 'flex',
              alignItems: 'center',
              fontSize: 14,
              fontWeight: 500,
              textDecoration: 'none',
              boxSizing: 'border-box',
              '&:hover': { backgroundColor: c.hover },
            }}
          >
            Sign up
          </Box>
        </Box>
      ) : null}
    </Box>
  );

  const links = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '.12em',
        textTransform: 'uppercase',
      }}
    >
      <Box
        component="nav"
        aria-label="Alliance apps"
        sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px' }}
      >
        {ALLIANCE_APP_ORDER.filter((app) => !isMobile || app !== key).map(
          (app) => (
            <Box
              key={app}
              component="a"
              href={ALLIANCE_APPS[app].url}
              sx={linkStyle(
                app === key
                  ? c.text
                  : LINK_HOVER[app][c.dark ? 'dark' : 'light'],
                app === key,
              )}
            >
              {ALLIANCE_APPS[app].name}
            </Box>
          ),
        )}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px' }}>
        {SIGN_IN_SOCIAL_LINKS.map((link) => (
          <Box
            key={link.label}
            component="a"
            href={link.href}
            target="_blank"
            rel="noopener noreferrer"
            sx={linkStyle(link.hover[c.dark ? 'dark' : 'light'])}
          >
            {link.label}
          </Box>
        ))}
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <Box
        sx={{
          minHeight: '100dvh',
          backgroundColor: c.bg,
          color: c.text,
          letterSpacing: 'normal',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            height: 176,
            flex: 'none',
            backgroundColor: CURRENT_APP.bar,
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <AllianceLogo height={60} width={150} aria-label="The Alliance" />
        </Box>
        <Box
          component="main"
          sx={{
            flex: 1,
            p: '32px 24px 28px',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          <Box sx={{ flex: 1 }}>{actions}</Box>
          {links}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100dvh',
        minHeight: 600,
        backgroundColor: c.bg,
        color: c.text,
        letterSpacing: 'normal',
        display: 'flex',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          width: '50%',
          flex: 'none',
          backgroundColor: CURRENT_APP.bar,
          color: '#ffffff',
          display: 'flex',
          flexDirection: 'column',
          p: '40px 56px',
          boxSizing: 'border-box',
        }}
      >
        <Box sx={{ display: 'flex' }}>
          <AllianceLogo height={52} width={130} aria-label="The Alliance" />
        </Box>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            component="figure"
            sx={{
              m: 0,
              // The square: half the window less padding, or what fits under
              // the logo with room for the quote (217px + its measured height;
              // ~317px for a one-line quote and meaning). The quote wraps at
              // the same width, so a floor keeps a long one from shrinking the
              // photo away
              width: `max(280px, min(calc(50vw - 112px), ${photoFit}))`,
              display: 'flex',
              flexDirection: 'column',
              gap: '28px',
            }}
          >
            <Box
              sx={{
                position: 'relative',
                // Short windows: the photo shrinks under the floor so the
                // caption still fits below the logo
                width: `min(100%, ${photoFit})`,
                aspectRatio: '1 / 1',
                borderRadius: '20px',
                overflow: 'hidden',
                backgroundColor: '#000000',
              }}
            >
              <Box
                component="img"
                src={props.photo.src}
                alt={props.photo.alt}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  fontFamily: "'Roboto Mono', monospace",
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '.1em',
                  color: '#ffffff',
                  backgroundColor: '#0000008c',
                  borderRadius: '8px',
                  p: '6px 10px',
                }}
              >
                {photoDateLabel(new Date())}
              </Box>
            </Box>
            <Box
              component="figcaption"
              ref={captionRef}
              sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
            >
              <Box
                component="blockquote"
                sx={{
                  m: 0,
                  fontSize: quoteFontSize(props.quote.text),
                  fontWeight: 700,
                  lineHeight: 1.25,
                  textWrap: 'pretty',
                }}
              >
                {props.quote.text}
              </Box>
              <Box
                component="p"
                sx={{
                  m: 0,
                  fontSize: 16,
                  lineHeight: 1.4,
                  color: '#ffffffd9',
                  textWrap: 'pretty',
                }}
              >
                {props.quote.meaning}
              </Box>
              <Box
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: '.14em',
                  textTransform: 'uppercase',
                  color: '#ffffffd9',
                }}
              >
                Andy-ism of the day
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,
          p: '40px 56px',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          overflowY: 'auto',
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {actions}
        </Box>
        {links}
      </Box>
    </Box>
  );
}
